import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Trash2, FileText, Trophy, AlertTriangle, Award, Users, ArrowRight, CalendarClock } from "lucide-react";
import { SharesPanel } from "@/components/app/boloes/SharesPanel";
import { PoolPrizesPanel } from "@/components/app/boloes/PoolPrizesPanel";
import { ExportGamesDialog } from "@/components/app/ExportGamesDialog";
import { toast } from "sonner";
import { gameSummary, type LotterySlug } from "@/lib/pool-math";
import { Checkbox } from "@/components/ui/checkbox";
import { LOTTERIES } from "@/hooks/use-lottery-selection";
import { cn, formatBRL } from "@/lib/utils";
import { GameDnaButton } from "@/components/app/GameDnaButton";
import { PoolScopeDialog } from "@/components/app/PoolScopeDialog";
import { describePoolScope, getActiveSpecials, type PoolScope, type SpecialDraw } from "@/lib/check-scope";
import { AddGamesDialog } from "@/components/app/boloes/AddGamesDialog";

export default function BolaoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>("games");
  const [scopeOpen, setScopeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [removeGamesOpen, setRemoveGamesOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<string[]>([]);
  const [removalRequestId, setRemovalRequestId] = useState<string | null>(null);

  const toggleGame = (gameId: string) =>
    setSelected((prev) => (prev.includes(gameId) ? prev.filter((x) => x !== gameId) : [...prev, gameId]));

  const removePoolFromCache = (poolId: string) => {
    qc.setQueriesData({ queryKey: ["pools"] }, (old: unknown) => {
      if (!Array.isArray(old)) return old;
      return old.filter((item) => (item as { id?: string }).id !== poolId);
    });
    qc.removeQueries({ queryKey: ["pool", poolId], exact: true });
    qc.removeQueries({ queryKey: ["pool-games", poolId], exact: true });
    qc.removeQueries({ queryKey: ["pool-shares-count", poolId], exact: true });
  };

  const { data: pool, isLoading } = useQuery({
    queryKey: ["pool", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pools")
        .select("*, lotteries(name, slug, universe_min, universe_max, numbers_drawn)")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: specials } = useQuery({
    queryKey: ["active-specials"],
    queryFn: getActiveSpecials,
  });

  const { data: games, isFetching: isFetchingGames } = useQuery({
    queryKey: ["pool-games", id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pool_games")
        .select("id, position, numbers, last_hits, last_contest")
        .eq("pool_id", id!)
        .order("position");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: sharesCount } = useQuery({
    queryKey: ["pool-shares-count", id],
    enabled: !!id,
    queryFn: async () => {
      const { count } = await supabase
        .from("pool_shares")
        .select("*", { count: "exact", head: true })
        .eq("pool_id", id!);
      return count ?? 0;
    },
  });

  const lotteryConfig = useMemo(() => {
    if (!pool?.lotteries) return null;
    return LOTTERIES.find((l) => l.slug === pool.lotteries.slug);
  }, [pool]);

  const bestResult = useMemo(() => {
    if (!games || games.length === 0) return null;
    const withHits = games.filter((g) => g.last_hits != null);
    if (withHits.length === 0) return null;
    return withHits.reduce((max, g) => (g.last_hits! > (max?.last_hits ?? -1) ? g : max), withHits[0]);
  }, [games]);

  const remove = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("Bolão inválido");
      const { data, error } = await supabase.rpc("delete_pool", { _pool_id: id });
      if (error) throw error;
      const result = data as { deleted?: boolean; reason?: string } | null;
      if (result?.deleted !== true && result?.reason !== "not_found") {
        throw new Error("O banco de dados não confirmou a exclusão do bolão");
      }
      return { deletedId: id, alreadyRemoved: result?.reason === "not_found" };
    },
    onSuccess: async ({ deletedId, alreadyRemoved }) => {
      setDeleteOpen(false);
      removePoolFromCache(deletedId);
      await qc.invalidateQueries({ queryKey: ["pools"], refetchType: "all" });
      removePoolFromCache(deletedId);
      toast.success(alreadyRemoved ? "Bolão removido da lista" : "Bolão excluído");
      navigate("/app/boloes", { replace: true });
    },
    onError: (e: any) => toast.error(e?.message ? `Erro ao excluir: ${e.message}` : "Erro ao excluir"),
  });

  const removeGames = useMutation({
    mutationFn: async () => {
      if (!id || pendingRemoval.length === 0) throw new Error("Nenhum jogo selecionado");
      const gameIds = [...pendingRemoval];
      const requestId = removalRequestId ?? crypto.randomUUID();
      if (!removalRequestId) setRemovalRequestId(requestId);
      const { data, error } = await supabase.functions.invoke("confirm-pool-removal", {
        body: { poolId: id, requestId, gameIds },
      });
      if (error) {
        const details = await error.context?.json?.().catch(() => null);
        throw new Error(details?.error || error.message);
      }
      return {
        ...(data as { removed?: number; duplicateRequest?: boolean; emailsFailed?: number; recipientsWithoutEmail?: number }),
        gameIds,
      };
    },
    onSuccess: (data) => {
      qc.setQueryData(["pool-games", id], (current: typeof games) =>
        current?.filter((game) => !data.gameIds.includes(game.id)),
      );
      setRemoveGamesOpen(false);
      setSelected([]);
      setPendingRemoval([]);
      setRemovalRequestId(null);
      toast.success(data?.duplicateRequest ? "Atualização já confirmada" : `${data?.removed ?? 0} jogo(s) removido(s) do bolão`);
      if (data?.emailsFailed) toast.error(`${data.emailsFailed} aviso(s) não puderam ser enviados`);
      if (data?.recipientsWithoutEmail) toast.warning(`${data.recipientsWithoutEmail} cotista(s) sem e-mail cadastrado`);
      qc.invalidateQueries({ queryKey: ["pool", id] });
      qc.invalidateQueries({ queryKey: ["pool-games", id], refetchType: "active" });
      qc.invalidateQueries({ queryKey: ["pools"] });
    },
    onError: (e: any) => toast.error(e?.message ? `Erro ao remover: ${e.message}` : "Erro ao remover jogos"),
  });

  const exportTXT = () => {
    if (!pool || !games) return;
    const header = `# ${pool.name}\n# ${pool.lotteries.name} • ${games.length} jogos • ${formatBRL(Number(pool.total_cost))}\n\n`;
    const body = games.map((g, i) =>
      `Jogo ${String(i + 1).padStart(3, "0")}: ${g.numbers.map((n: number) => String(n).padStart(2, "0")).join(" - ")}`,
    ).join("\n");
    const blob = new Blob([header + body], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bolao-${pool.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <Skeleton className="h-96" />;
  }

  if (!pool) {
    return (
      <div className="text-center py-16">
        <p className="text-muted-foreground">Bolão não encontrado</p>
        <Button asChild className="mt-4"><Link to="/app/boloes">Voltar</Link></Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to="/app/boloes"><ArrowLeft className="mr-1 h-4 w-4" /> Bolões</Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl">{pool.name}</h1>
            <p className="text-sm text-muted-foreground">
              {pool.lotteries?.name} • {pool.total_games} jogos • {formatBRL(Number(pool.total_cost))}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={exportTXT}><FileText className="mr-2 h-4 w-4" /> TXT</Button>
            {games && games.length > 0 && (
              <ExportGamesDialog
                lotterySlug={pool.lotteries.slug as "mega-sena" | "lotofacil" | "quina" | "dupla-sena"}
                lotteryName={pool.lotteries.name}
                games={games.map((g) => g.numbers)}
                brandLine={`LotoTerminal • ${pool.name}`}
                costPerGame={Number(pool.cost_per_game)}
                filenameBase={`bolao-${pool.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              />
            )}
            <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="mr-2 h-4 w-4" /> Excluir
            </Button>
            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir este bolão?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Todos os jogos, cotistas e conferências associados serão removidos. Esta ação não pode ser desfeita.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={remove.isPending}
                    onClick={(e) => { e.preventDefault(); remove.mutate(); }}
                  >
                    {remove.isPending ? "Excluindo…" : "Excluir"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          label="Modo"
          value={
            pool.generation_mode === "complete"
              ? "Completo"
              : pool.generation_mode === "closure"
              ? "Fechamento"
              : pool.generation_mode === "pairs"
              ? "Duplas"
              : pool.generation_mode === "fixed"
              ? "Fixas + variáveis"
              : pool.generation_mode === "smart"
              ? "Gerador inteligente"
              : pool.generation_mode === "manual"
              ? "Montado por você"
              : pool.generation_mode === "mixed"
              ? "Misto (livre + gerado)"
              : pool.generation_mode
          }
        />
        <StatCard label="Dezenas escolhidas" value={String(pool.selected_numbers.length)} />
        <StatCard label="Por jogo" value={`${pool.numbers_per_game} dezenas`} />
        <StatCard
          label="Meta"
          value={pool.generation_mode === "closure" ? `${pool.guarantee_hits} se ${pool.guarantee_if}` : "—"}
        />
      </div>

      {bestResult && (
        <Card className="border-primary/30 bg-lt-gold-bg">
          <CardContent className="flex items-center gap-4 pt-6">
            <Trophy className="h-8 w-8 text-primary" />
            <div>
              <div className="font-serif text-lg">Melhor resultado</div>
              <div className="text-sm text-muted-foreground">
                Jogo #{bestResult.position} acertou <strong>{bestResult.last_hits}</strong> dezenas no concurso {bestResult.last_contest}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-lt-border">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
          <div className="flex items-start gap-3">
            <CalendarClock className="h-5 w-5 text-lt-teal" />
            <div>
              <div className="font-serif text-lg">Concurso dos jogos</div>
              <div className="text-sm text-muted-foreground">
                {describePoolScope(
                  pool.check_scope as PoolScope,
                  pool.check_from_contest,
                  pool.check_until_contest,
                  (specials ?? []).find((s: SpecialDraw) => s.id === pool.special_schedule_id)?.special_name,
                )}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setScopeOpen(true)}>
            <CalendarClock className="mr-2 h-4 w-4" /> Alterar concurso
          </Button>
        </CardContent>
      </Card>

      <PoolScopeDialog
        open={scopeOpen}
        onOpenChange={setScopeOpen}
        lotterySlug={pool.lotteries.slug}
        initialScope={(pool.check_scope as PoolScope) ?? "next_only"}
        onConfirm={async ({ scope, from, until, specialScheduleId }) => {
          const { error } = await supabase.rpc("update_pool_check_scope", {
            _pool_id: pool.id,
            _scope: scope,
            _from: from,
            _until: until,
            _special_schedule_id: specialScheduleId ?? undefined,
          });
          if (error) {
            toast.error(error.message);
            throw error;
          }
          toast.success("Concurso de todos os jogos atualizado");
          qc.invalidateQueries({ queryKey: ["pool", id] });
          qc.invalidateQueries({ queryKey: ["pool-games", id] });
        }}
      />

      {sharesCount === 0 && (
        <Card className="border-2 border-lt-teal/60 bg-lt-teal/5">
          <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-lt-teal/20 p-2">
                <Users className="h-5 w-5 text-lt-teal" />
              </div>
              <div>
                <div className="font-serif text-lg text-foreground">
                  Adicione os participantes do bolão
                </div>
                <div className="text-sm text-muted-foreground">
                  Cadastre os cotistas para dividir o custo de{" "}
                  <strong>{formatBRL(Number(pool.total_cost))}</strong>{" "}
                  e acompanhar quem já pagou. O sistema calcula o valor de cada cota automaticamente.
                </div>
              </div>
            </div>
            <Button
              onClick={() => setActiveTab("shares")}
              className="shrink-0 bg-lt-teal text-background hover:bg-lt-teal/90"
            >
              <Users className="mr-2 h-4 w-4" /> Adicionar cotistas
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="games">Jogos ({games?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="prizes">
            <Award className="mr-1 h-3.5 w-3.5" /> Prêmios
          </TabsTrigger>
          <TabsTrigger value="shares" className="relative">
            <Users className="mr-1 h-3.5 w-3.5" />
            Cotistas {sharesCount ? `(${sharesCount})` : ""}
            {sharesCount === 0 && (
              <span className="ml-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-lt-teal" />
            )}
          </TabsTrigger>
          <TabsTrigger value="numbers">Dezenas</TabsTrigger>
        </TabsList>

        <TabsContent value="games">
          <Card>
            <CardContent className="pt-6">
              <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
                {games && games.length > 0 && (
                  <>
                    <span className="mr-auto text-xs text-muted-foreground">
                       {pendingRemoval.length > 0
                         ? `${pendingRemoval.length} exclusão(ões) pendente(s) · ${selected.length} selecionado(s)`
                         : selected.length > 0
                         ? `${selected.length} jogo(s) selecionado(s)`
                        : "Marque os jogos para remover"}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setSelected(selected.length === games.length ? [] : games.map((g) => g.id))
                      }
                    >
                      {selected.length === games.length ? "Limpar seleção" : "Selecionar todos"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={selected.length === 0 || isFetchingGames || removeGames.isPending}
                       onClick={() => {
                         setPendingRemoval((prev) => [...new Set([...prev, ...selected])]);
                         setRemovalRequestId(null);
                         setSelected([]);
                       }}
                    >
                       <Trash2 className="mr-2 h-4 w-4" /> Marcar para excluir
                    </Button>
                  </>
                )}
                <AddGamesDialog
                  poolId={pool.id}
                  lotteryId={pool.lottery_id}
                  lotterySlug={pool.lotteries.slug as LotterySlug}
                  existingGames={(games ?? []).map((g) => g.numbers as number[])}
                  contestNumber={pool.check_from_contest}
                />
              </div>

               {pendingRemoval.length > 0 && (
                 <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border border-destructive/30 bg-destructive/5 p-3 text-sm">
                   <span>{pendingRemoval.length} jogo(s) marcados para exclusão. Ainda estão válidos até confirmar.</span>
                   <div className="flex gap-2">
                     <Button variant="ghost" size="sm" disabled={removeGames.isPending} onClick={() => { setPendingRemoval([]); setRemovalRequestId(null); }}>Cancelar</Button>
                     <Button size="sm" disabled={removeGames.isPending || isFetchingGames} onClick={() => setRemoveGamesOpen(true)}>Atualizar Bolão</Button>
                   </div>
                 </div>
               )}

              <AlertDialog open={removeGamesOpen} onOpenChange={setRemoveGamesOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                     <AlertDialogTitle>Atualizar Bolão e excluir {pendingRemoval.length} jogo(s)?</AlertDialogTitle>
                    <AlertDialogDescription>
                       Os jogos serão excluídos apenas após a confirmação. O custo será recalculado e os cotistas com e-mail cadastrado receberão a relação completa dos jogos válidos.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      disabled={removeGames.isPending}
                      onClick={(e) => { e.preventDefault(); removeGames.mutate(); }}
                    >
                       {removeGames.isPending ? "Atualizando…" : "Atualizar Bolão"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              {!games || games.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  <AlertTriangle className="mx-auto mb-2 h-6 w-6" />
                  Sem jogos
                </div>
              ) : (
                <div className="max-h-[600px] space-y-1 overflow-y-auto">
                  {games.map((g) => {
                    const summary = gameSummary(g.numbers);
                    return (
                      <div
                        key={g.id}
                        className={cn(
                          "flex flex-wrap items-center gap-3 rounded-md border border-lt-border bg-lt-bg2 p-2.5 text-[13px]",
                           (selected.includes(g.id) || pendingRemoval.includes(g.id)) && "border-destructive/60 bg-destructive/5",
                        )}
                      >
                        <Checkbox
                           checked={selected.includes(g.id) || pendingRemoval.includes(g.id)}
                           disabled={pendingRemoval.includes(g.id) || removeGames.isPending}
                          onCheckedChange={() => toggleGame(g.id)}
                          aria-label={`Selecionar jogo ${g.position}`}
                        />
                        <span className="w-12 text-xs text-muted-foreground">#{String(g.position).padStart(3, "0")}</span>
                        <div className="flex flex-1 flex-wrap gap-1">
                          {g.numbers.map((n: number) => (
                            <span key={n} className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-[11px] font-medium text-primary">
                              {String(n).padStart(2, "0")}
                            </span>
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          Σ {summary.sum} • {summary.evens}P/{summary.odds}I
                        </span>
                        {g.last_hits != null && (
                          <span className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
                            g.last_hits >= (pool.lotteries?.numbers_drawn ?? 6) - 2
                              ? "bg-primary text-primary-foreground"
                              : "bg-lt-teal/20 text-lt-teal",
                          )}>
                            {g.last_hits} acertos
                          </span>
                        )}
                        <GameDnaButton numbers={g.numbers} />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="shares">
          <SharesPanel poolId={pool.id} totalCost={Number(pool.total_cost)} />
        </TabsContent>

        <TabsContent value="prizes">
          <PoolPrizesPanel
            poolId={pool.id}
            lotteryId={pool.lottery_id}
            lotterySlug={pool.lotteries.slug}
          />
        </TabsContent>

        <TabsContent value="numbers">
          <Card>
            <CardHeader><CardTitle>Dezenas escolhidas</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {pool.selected_numbers.map((n: number) => (
                  <span key={n} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground font-medium">
                    {String(n).padStart(2, "0")}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-1 font-serif text-xl">{value}</div>
      </CardContent>
    </Card>
  );
}
